<template>
  <div>
    <v-card class="mb-7" flat>
      <v-card-title> O que aconteceu? </v-card-title>

      <v-row class="pa-4" align="center" no-gutters>
        <v-col cols="12" sm="4" md="3" lg="2">
          <v-text-field
            v-model="date"
            label="Data do sinistro"
            type="date"
            :color="insurer.color"
            outlined
            dense
          />
        </v-col>
      </v-row>

      <v-card-text>
        <p>Descreva de forma simples o que ocorreu.</p>

        <v-alert type="info" color="info darken-3" dense text>
          Pode escrever ou utilizar o seu microfone para ditar a descrição do
          seu sinistro.
        </v-alert>

        <v-row>
          <v-col>
            <v-textarea
              v-model="participation"
              outlined
              placeholder="Exemplo: No dia 08/04, após uma falha de energia no edifício, o equipamento deixou de funcionar..."
              hint="Esta informação será utilizada apenas para análise do sinistro."
              :color="insurer.color"
              auto-grow
              :readonly="recording"
              hide-details="auto"
            >
              <template #append>
                <div v-if="speechRecognitionEnabled">
                  <v-btn
                    @click="handleClickStopRecording()"
                    v-if="recording"
                    icon
                    style="margin-top: -5px"
                    :color="insurer.color"
                  >
                    <v-icon>mdi-stop-circle</v-icon>
                  </v-btn>

                  <v-btn
                    @click="handleClickStartRecording()"
                    v-else
                    icon
                    style="margin-top: -5px"
                  >
                    <v-icon>mdi-microphone</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-textarea>
          </v-col>
        </v-row>

        <v-expand-transition>
          <div v-if="pendingFlow" class="mt-5">
            <v-card outlined>
              <v-card-title class="d-flex align-center">
                Tipo de sinistro
                <v-spacer />
                <v-btn
                  icon
                  @click="pendingFlow = null"
                  :disabled="loadingDecision"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text>
                <div class="mb-2 text-body-1">
                  Pela sua descrição, consideramos ser um sinistro de:
                  <span
                    class="font-weight-bold"
                    :style="{ color: insurer.color }"
                  >
                    {{
                      pendingFlow === "DNA"
                        ? "Danos por Água"
                        : "Riscos Elétricos"
                    }}
                  </span>
                </div>

                <div class="text-caption grey--text">
                  Se estiver incorrecto, pode alterar antes de continuar.
                </div>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  outlined
                  :color="insurer.color"
                  @click="toggleFlowAndContinue"
                  :disabled="loadingDecision"
                >
                  Alterar para
                  {{
                    toggleFlow(pendingFlow) === "DNA"
                      ? "Danos por Água"
                      : "Riscos Elétricos"
                  }}
                </v-btn>

                <v-spacer />

                <v-btn
                  :color="insurer.color"
                  dark
                  @click="confirmFlow"
                  :loading="loadingDecision"
                >
                  Continuar
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
        </v-expand-transition>
      </v-card-text>
    </v-card>

    <v-btn
      v-if="!pendingFlow"
      :color="insurer.color"
      :dark="validated"
      :disabled="!validated || loadingDecision"
      :loading="loadingDecision"
      @click="handleClickContinue"
    >
      Continuar
    </v-btn>

    <v-alert v-if="loadingDecision" type="info" dense text class="mt-4">
      Estamos analisando a descrição do sinistro.
    </v-alert>

    <v-alert v-if="decisionError" type="warning" dense text class="mt-4">
      {{ decisionError }}
    </v-alert>
  </div>
</template>

<script>
import { decideFlow } from "@/services/customer_inbound";

export default {
  props: {
    insurer: Object,
  },

  data() {
    return {
      participation: "",
      date: "",
      recognition: null,
      speechRecognitionEnabled: false,
      speechRecognition: null,
      recording: false,
      loadingDecision: false,
      decisionError: null,
      pendingFlow: null,
    };
  },

  computed: {
    validated() {
      return !!this.participation && !!this.date;
    },
  },

  watch: {
    participation() {
      this.pendingFlow = null;
      this.decisionError = null;
    },
    date() {
      this.decisionError = null;
    },
  },

  methods: {
    toggleFlow(flow) {
      return flow === "DNA" ? "RE" : "DNA";
    },

    continueWithFlow(flow) {
      localStorage.setItem("claim_flow", flow);
      this.$emit("continue", { flow });
    },

    confirmFlow() {
      if (!this.pendingFlow) return;
      this.continueWithFlow(this.pendingFlow);
    },

    toggleFlowAndContinue() {
      if (!this.pendingFlow) return;
      const newFlow = this.toggleFlow(this.pendingFlow);
      this.pendingFlow = newFlow;
      this.continueWithFlow(newFlow);
    },

    checkSpeechRecognition() {
      this.speechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      this.speechRecognitionEnabled = !!this.speechRecognition;
      if (!this.speechRecognitionEnabled) return;

      this.recognition = new SpeechRecognition();

      this.recognition.continuous = false;
      this.recognition.interimResults = true;
      this.recognition.maxAlternatives = 1;

      this.recognition.onresult = (event) => {
        this.participation = event.results[0][0].transcript;
      };

      this.recognition.addEventListener("end", () => {
        this.recording = false;
      });
    },

    handleClickStartRecording() {
      this.recording = true;
      this.recognition.start();
    },

    handleClickStopRecording() {
      this.recording = false;
      this.recognition.stop();
    },

    loadParticipation() {
      const participation = localStorage.getItem("participation");
      if (participation) this.participation = participation;
    },

    loadDate() {
      const date = localStorage.getItem("date");
      if (date) this.date = date;
    },

    async handleClickContinue() {
      this.loadingDecision = true;
      this.decisionError = null;

      try {
        localStorage.setItem("participation", this.participation);
        localStorage.setItem("date", this.date);

        const shortcut = this.participation.trim().toUpperCase();

        if (shortcut === "DNA") {
          this.pendingFlow = "DNA";
          return;
        }

        if (shortcut === "RE") {
          this.pendingFlow = "RE";
          return;
        }

        const data = await decideFlow(this.participation);

        if (data.flow === "DNA" || data.flow === "RE") {
          this.pendingFlow = data.flow;
          return;
        }

        this.decisionError =
          "A descrição do ocorrido não corresponde à apólice selecionada.";
      } catch (e) {
        const response = e?.response;

        if (response?.status === 422 && response?.data?.errors) {
          const errors = response.data.errors;
          if (errors.participation?.length) {
            this.decisionError = errors.participation[0];
            return;
          }
          const firstField = Object.keys(errors)[0];
          this.decisionError = errors[firstField][0];
          return;
        }

        console.error("Erro decide-flow", e);
        this.decisionError = "Erro ao analisar o sinistro. Tente novamente.";
      } finally {
        this.loadingDecision = false;
      }
    },
  },

  created() {
    this.checkSpeechRecognition();
    this.loadParticipation();
    this.loadDate();
  },
};
</script>
